import {
  eachDayOfInterval,
  getDaysInMonth,
  getISOWeek,
  lastDayOfYear,
  startOfYear,
  eachWeekOfInterval,
  endOfYear,
  getMonth,
  getYear,
} from 'date-fns';

interface IGetDaysPerWeek {
  month: number;
  label: string;
  weeks: number[];
  totalDays: number[];
}

interface ITransform {
  month: number;
  weeks: number[];
}

export const countOccurrences = (arr: number[], val: number): number => {
  return arr.reduce((a, v) => (v === val ? a + 1 : a), 0);
};

export const generateArray = (
  min: number,
  max: number,
  step: number,
): number[] => {
  const res: number[] = [];
  for (let i = 0; i < max; i += step) {
    if (i === 0) {
      res.push(min);
    } else {
      res.push(res[i - 1] + step);
    }
  }
  return res;
};

export const generateRandomData = (
  min: number,
  max: number,
  amount: number,
): number[] => {
  const data: number[] = [];
  for (let i = 0; i < amount; i += 1) {
    const val = Math.floor(Math.random() * (max - min)) + min;
    data.push(val);
  }

  return data;
};

export const sumArrays = (...arrays: number[][]): number[] => {
  const n = arrays.reduce((max, xs) => Math.max(max, xs.length), 0);
  const result = Array.from({ length: n });
  return result.map((_, i) =>
    arrays
      .map(xs => xs[i] || 0)
      .reduce((sum, x) => {
        if (x === -1) {
          return sum + 0;
        }
        return sum + x;
      }, 0),
  );
};

export const calculateAverage = (array: number[], countZero = true): number => {
  const arrayWithoutZeros = array.filter(val => val !== 0);

  const average = !countZero
    ? arrayWithoutZeros.reduce((a, b) => a + b, 0) / arrayWithoutZeros.length
    : array.reduce((a, b) => a + b, 0) / array.length;

  return average;
};

export const getTotalDaysPerWeek = (): IGetDaysPerWeek[] => {
  const data: IGetDaysPerWeek[] = [
    { month: 0, label: 'Jan', weeks: [], totalDays: [] },
    { month: 1, label: 'Feb', weeks: [], totalDays: [] },
    { month: 2, label: 'Mar', weeks: [], totalDays: [] },
    { month: 3, label: 'Apr', weeks: [], totalDays: [] },
    { month: 4, label: 'May', weeks: [], totalDays: [] },
    { month: 5, label: 'Jun', weeks: [], totalDays: [] },
    { month: 6, label: 'Jul', weeks: [], totalDays: [] },
    { month: 7, label: 'Aug', weeks: [], totalDays: [] },
    { month: 8, label: 'Sep', weeks: [], totalDays: [] },
    { month: 9, label: 'Oct', weeks: [], totalDays: [] },
    { month: 10, label: 'Nov', weeks: [], totalDays: [] },
    { month: 11, label: 'Dec', weeks: [], totalDays: [] },
  ];

  const currentYear = new Date().getFullYear();
  const daysOfYear = eachDayOfInterval({
    start: startOfYear(new Date(currentYear, 0, 1)),
    end: lastDayOfYear(new Date(currentYear, 0, 1)),
  });

  const whichWeek = daysOfYear.map(day => getISOWeek(new Date(day)));

  daysOfYear.forEach(day => {
    const week = getISOWeek(new Date(day));
    const month = new Date(day).getMonth();
    const monthIndex = data.findIndex(i => i.month === month);

    if (monthIndex !== -1) {
      const weekIndex = data[monthIndex].weeks.findIndex(wk => wk === week);
      if (weekIndex === -1) {
        const daysInMonth = getDaysInMonth(new Date(currentYear, month));
        const sumTotalDays = data[monthIndex].totalDays.reduce(
          (a, b) => a + b,
          0,
        );
        const count = countOccurrences(whichWeek, week);

        data[monthIndex].weeks.push(week);

        if (
          sumTotalDays + count > daysInMonth &&
          sumTotalDays !== daysInMonth
        ) {
          data[monthIndex].totalDays.push(daysInMonth - sumTotalDays);
          if (monthIndex < 11) {
            data[monthIndex + 1].totalDays.push(
              sumTotalDays + count - daysInMonth,
            );
          }
        } else if (sumTotalDays !== daysInMonth) {
          data[monthIndex].totalDays.push(count);
        }
      }
    }
  });

  return data;
};

export const getOrderPriorityTranslation = (orderStatus: string): string => {
  switch (orderStatus) {
    case 'BAIXA':
      return 'components:charts.low';
    case 'MEDIA':
      return 'components:charts.average';
    case 'ALTA':
      return 'components:charts.high';
    case 'URGENTE':
      return 'components:charts.urgent';
    default:
      return 'components:charts.low';
  }
};

export const transformWeeksToMonths = (
  weeks: number[],
  year: number,
  countZeros = true,
): number[] => {
  const date = new Date(year, 1, 1);
  const startYear = startOfYear(date);
  const endYear = endOfYear(date);
  const eachWeek = eachWeekOfInterval({
    start: startYear,
    end: endYear,
  });

  const data: ITransform[] = [
    { month: 0, weeks: [] },
    { month: 1, weeks: [] },
    { month: 2, weeks: [] },
    { month: 3, weeks: [] },
    { month: 4, weeks: [] },
    { month: 5, weeks: [] },
    { month: 6, weeks: [] },
    { month: 7, weeks: [] },
    { month: 8, weeks: [] },
    { month: 9, weeks: [] },
    { month: 10, weeks: [] },
    { month: 11, weeks: [] },
  ];

  data.forEach(dt => {
    eachWeek.forEach((wk, idx) => {
      const wkMonth = getMonth(wk);
      const isSameMonth = wkMonth === dt.month && getYear(wk) === year;
      if (isSameMonth) {
        dt.weeks.push(weeks[idx]);
      }
    });
  });

  const formattedData = data.map(dt => ({
    month: dt.month,
    average: Math.ceil(calculateAverage(dt.weeks, countZeros)),
  }));

  const monthData = formattedData.map(dt => dt.average);

  return monthData;
};

export const getMax = (array: number[]): number => {
  return array.reduce((a, b) => Math.max(a, b), 0);
};
